<template>
    <div id="map">
        <div class="title">{{ title }}</div>
        <div class="subtitle">
            Total number of calls in
            <b>{{
                options.callType.toLowerCase() +
                    (options.callType === "All Categories" ? "*" : "")
            }}</b>
            across RI
        </div>
        <div class="description">
            Accumulating calls
            <b>{{ options.startDate }}</b> -
            <b>{{ options.endDate }}</b>
            <br />Click on any map tile to focus on that region
        </div>
        <div id="legend_wrapper">
            <div id="table_wrapper">
                <table>
                    <tr>
                        <td class="row_header">Total calls:</td>
                        <td>{{ total }}</td>
                    </tr>
                    <tr>
                        <td class="row_header">From RI:</td>
                        <td>{{ inState }}</td>
                    </tr>
                    <tr>
                        <td class="row_header">
                            Out-of-state/Location Unknown:
                        </td>
                        <td>{{ outOfState }}</td>
                    </tr>
                </table>
            </div>
            <div id="legend">
                <div id="all_areas">
                    <a
                        href="#"
                        v-on:click.stop.prevent="
                            $emit('listChange', {
                                source: 'city',
                                value: 'All Areas',
                            })
                        "
                    >
                        <font-awesome-icon icon="undo-alt" />Back to all areas
                    </a>
                </div>
                <div id="gradient"></div>
                <div id="legend_text_wrapper">
                    <div>Fewer</div>
                    <div>More calls</div>
                </div>
                <div>(Adjusted for population)</div>
            </div>
        </div>
        <svg id="svg_map" />
        <div class="note" v-if="options.callType === 'All Categories'">
            {{ "*" }} Calls that fall under multiple categories counted once.
        </div>
        <div class="tooltip"></div>
    </div>
</template>

<script>
import * as d3 from "d3";
import moment from "moment";
import { mapState } from "vuex";

export default {
    name: "MapRI",
    data() {
        return {
            map: null,
            filteredData: null,
            total: null,
            inState: null,
            outOfState: null,
        };
    },
    props: {
        title: String,
        mapShape: Array,
        pops: Object,
    },
    computed: {
        ...mapState({
            options: state => state.options,
        }),
    },
    mounted() {
        this.filteredData = this.filterData(this.options);
        this.updateTable(this.filteredData, this.options);
        this.map = this.initMap();
    },
    methods: {
        filterData(options) {
            if (options.startDate == undefined || options.endDate == undefined)
                return null;

            let high = options.endDate;
            let low = options.startDate;

            if (new moment.utc(high).isBefore(new moment.utc(low))) return null;

            let columns = this.$store.state.rawData.columns;

            let mapData = this.$store.state.rawData
                .filter(d => d.type === options.callType)
                .map(d => ({
                    city: d.city,
                    type: d.type,
                    values: d3.sum(
                        d.values.slice(
                            columns.indexOf(low) == -1
                                ? 0
                                : columns.indexOf(low),
                            (columns.indexOf(high) == -1
                                ? columns.length
                                : columns.indexOf(high)) + 1
                        )
                    ),
                }));

            return mapData;
        },

        updateTable(mapData, options) {
            if (!mapData) return;

            this.total =
                options.callType === "All Categories"
                    ? d3.sum(mapData, d => d.values)
                    : mapData.find(d => d.city === "All Areas").values;

            this.outOfState = mapData.find(d => d.city === "Other").values;

            this.inState = this.total - this.outOfState;
        },

        initMap() {
            const shape = this.mapShape;

            const projection = d3.geoTransverseMercator()
                .rotate([70.65 + 30 / 60, -41.64 - 5 / 60])
                .scale(47000);

            const path = d3.geoPath().projection(projection);

            let svg = d3.select("#svg_map").attr("viewBox", "0 0 550 620");

            svg.selectAll("path")
                .data(shape)
                .enter()
                .append("path")
                .attr(
                    "id",
                    d =>
                        "city" +
                        d.id
                            .replace(" ", "")
                            .toLowerCase()
                            .substring(0, 6)
                )
                .attr("d", path)
                .attr("fill", "white")
                .attr("class", "city")
                .on("click", d =>
                    this.$emit("listChange", {
                        source: "city",
                        value: d.id.replace(
                            /\w\S*/g,
                            txt =>
                                txt.charAt(0).toUpperCase() +
                                txt.substr(1).toLowerCase()
                        ),
                    })
                );

            let legendData = d3.quantize(
                d3.interpolateRgb("#ffffff", "#114D93"),
                6
            );

            d3.select("#gradient")
                .selectAll("rect")
                .data(legendData)
                .enter()
                .append("span")
                .attr("class", "legend_span")
                .style("background-color", d => d);

            function handleMouseOver(d, options) {
                let html =
                    "City/Town: " +
                    d.city +
                    "<br />" +
                    "Type: " +
                    options.callType +
                    "<br />" +
                    "Call Counts: " +
                    d.values;

                d3.select(".tooltip")
                    .html(html)
                    .style("top", event.pageY - 10 + "px")
                    .style("left", event.pageX + 10 + "px")
                    .style("visibility", "visible");
            }

            function handleMouseOut() {
                d3.select(".tooltip").style("visibility", "hidden");
            }

            function updateMap(mapData, options, pops) {
                if (!mapData) return;

                if (options.callType !== "All Categories") {
                    mapData = mapData.filter(d => d.city !== "All Areas");
                }

                let dataRangePer1000 = d3.extent(
                    mapData.map(d => d.values / (pops[d.city] / 1000))
                );

                let colorScale = d3
                    .scaleSequential()
                    .domain(dataRangePer1000)
                    .interpolator(d3.interpolateRgb("#ffffff", "#114D93"));

                mapData.forEach(d =>
                    d3
                        .select(
                            "path#city" +
                                d.city
                                    .replace(" ", "")
                                    .toLowerCase()
                                    .substring(0, 6)
                        )
                        .on("mouseover", () => handleMouseOver(d, options))
                        .on("mouseout", handleMouseOut)
                        .transition()
                        .duration(1000)
                        .attr(
                            "fill",
                            colorScale(d.values / (pops[d.city] / 1000))
                        )
                );
            }

            updateMap(this.filteredData, this.options, this.pops);

            return {
                updateMap,
            };
        },
    },
    watch: {
        options: {
            deep: true,
            handler(newOptions) {
                this.filteredData = this.filterData(newOptions);
                this.updateTable(this.filteredData, newOptions);
                this.map.updateMap(this.filteredData, newOptions, this.pops);
            },
        },
    },
};
</script>

<style lang="sass">
.city
  stroke: #bbb
  stroke-width: 1
  cursor: pointer

.city:hover
  fill: #fe230a
  transition: fill 0.5s

.tooltip
  background: rgba(0, 0, 0, 0.5)
  padding: 6px
  border-radius: 4px
  position: absolute
  z-index: 10
  visibility: hidden
  color: white
  font-family: sans-serif
  font-size: 1rem
  text-align: left

#svg_map .legendtext
  color: #222
  font-size: 0.6rem

#legend_wrapper
  display: flex
  flex-flow: row wrap
  justify-content: space-between
  align-items: stretch
  color: #222
  font-size: 0.9rem
  padding: 5px 0
  margin: 0

#legend
  text-align: right
  width: max-content
  font-size: 0.8rem

table tr td
  padding: 0
  margin: 0

.row_header
  font-weight: bold
  width: 160px

.legend_span
  display: inline-block
  width: 25px
  margin-top: 10px
  height: 10px
  border: 1px solid #bbb

#legend_text_wrapper
  display: flex
  flex: row wrap
  justify-content: space-between

#all_areas a
  color: #222
  font-size: 0.9rem
  text-decoration: none
  padding: 0 0 3px 5px
  cursor: pointer

  & svg
    margin-right: 5px

  &:hover
    color: #fe230a
    fill: #fe230a
</style>
